import { assign } from "@suse-o11y/assign";
import { IconCloseOutlined } from "@suse-o11y/icons";
import { Logger } from "@suse-o11y/logger";
import { noop } from "@suse-o11y/noop";
import { Modal as AntModal, type ModalProps as AntdModalProps } from "antd/es";
import type { LegacyButtonType, NativeButtonProps } from "antd/es/button/button";
import React, { type FC, useEffect, useRef, useState } from "react";

import { RelativeContainer } from "../RelativeContainer";

import type { ModalSize } from "./modal-size";
import { getModalWidth } from "./modal-size";

const AntModalWithChildren: React.FC<React.PropsWithChildren<AntdModalProps>> = AntModal;

type ModalChild = (close: () => void) => React.ReactNode;

const logger = Logger.namespace("Modal");

export interface ModalProps {
	className?: string;
	open?: boolean;
	footer?: ModalChild | React.ReactNode;
	header?: ModalChild | React.ReactNode;
	onCloseRequested?: () => boolean | void | PromiseLike<any>;
	onClose?: () => void;
	children?: ModalChild | React.ReactNode;
	keyboard?: boolean;
	zIndex?: number;
	maskClosable?: boolean;
	size?: number | ModalSize;
	transitionName?: string;
	maskTransitionName?: string;
	bodyStyle?: React.CSSProperties;
}

declare type GetContainerFunc = () => HTMLElement;

export interface ModalFuncProps {
	prefixCls?: string;
	className?: string;
	visible?: boolean;
	title?: React.ReactNode;
	content?: React.ReactNode;
	onOk?: (...args: Array<any>) => any;
	onCancel?: (...args: Array<any>) => any;
	okButtonProps?: NativeButtonProps;
	cancelButtonProps?: NativeButtonProps;
	centered?: boolean;
	width?: string | number;
	iconClassName?: string;
	okText?: React.ReactNode;
	okType?: LegacyButtonType;
	cancelText?: React.ReactNode;
	icon?: React.ReactNode;
	iconType?: string;
	mask?: boolean;
	maskClosable?: boolean;
	zIndex?: number;
	okCancel?: boolean;
	style?: React.CSSProperties;
	maskStyle?: React.CSSProperties;
	type?: "error" | "success" | "warning" | "info" | "warn" | "confirm";
	keyboard?: boolean;
	getContainer?: string | HTMLElement | GetContainerFunc | false;
	autoFocusButton?: null | "ok" | "cancel";
	transitionName?: string;
	maskTransitionName?: string;
	bodyStyle?: React.CSSProperties;
}

const modalImpl: FC<ModalProps> = ({
	className,
	open = true,
	footer = null,
	header,
	onCloseRequested = noop as NonNullable<ModalProps["onCloseRequested"]>,
	onClose = noop,
	children,
	keyboard,
	zIndex,
	maskClosable = true,
	size = "medium",
	transitionName,
	maskTransitionName,
	bodyStyle,
}) => {
	const [closing, setClosing] = useState(false);
	const ignoreCloseRef = useRef(false);

	useEffect(
		() => () => {
			ignoreCloseRef.current = true;
		},
		[],
	);

	const close = (): void => {
		if (!ignoreCloseRef.current) {
			setClosing(true);
		}
	};

	const requestClose = (): void => {
		const closeRequestResult = onCloseRequested();

		// If the function returns false we don't want to close anything
		if (closeRequestResult === false) {
			return;
		}

		// If true/undefined was returned then we just close the dialog
		if (closeRequestResult === true || !closeRequestResult) {
			close();
		} else {
			closeRequestResult.then(
				() => {
					close();
				},
				(error) => {
					logger.error("closeRequestResult was rejected. Reason:", error);
				},
			);
		}
	};

	const onAfterClose = (): void => {
		setClosing(false);
		onClose();
	};

	return (
		<AntModalWithChildren
			afterClose={onAfterClose}
			bodyStyle={bodyStyle}
			centered={true}
			className={className}
			closeIcon={<IconCloseOutlined className="rounded-sm p-2 hover:text-primary-500" />}
			destroyOnClose
			footer={renderChild(footer, requestClose)}
			keyboard={keyboard}
			maskClosable={maskClosable}
			maskTransitionName={maskTransitionName}
			onCancel={requestClose}
			title={renderChild(header, requestClose)}
			transitionName={transitionName}
			visible={!closing && open}
			width={getModalWidth(size)}
			zIndex={zIndex}
		>
			<RelativeContainer>{renderChild(children, requestClose)}</RelativeContainer>
		</AntModalWithChildren>
	);
};

export const Modal = assign(modalImpl, {
	info: (props: ModalFuncProps) => AntModal.info(props),
	success: (props: ModalFuncProps) => AntModal.success(props),
	error: (props: ModalFuncProps) => AntModal.error(props),
	warn: (props: ModalFuncProps) => AntModal.warn(props),
	warning: (props: ModalFuncProps) => AntModal.warning(props),
	confirm: (props: ModalFuncProps) => AntModal.confirm(props),
});

// The child.call(null) is there because TypeScript will complain about
// "Function" and "(() => React.ReactNode)" to not have a call signature :/
const renderChild = (child: ModalChild | React.ReactNode, close: () => void): React.ReactNode =>
	typeof child === "function" ? child.call(null, close) : child;
